import { router } from "@inertiajs/react"
import { UserPlus } from "lucide-react"
import type { ReactNode } from "react"

import { AdminLayout } from "@/layouts/AdminLayout"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import Pagination from "@/components/Pagination"
import type { GuestProfileRow, PaginationMeta, PropertySummary } from "@/lib/types"

interface Props {
  property: PropertySummary
  profiles: GuestProfileRow[]
  meta: PaginationMeta
  urls: {
    index: string
    create: string
  }
}

function RepeatBadge({ staysCount }: { staysCount: number }) {
  if (staysCount < 2) return null
  return <Badge variant="secondary" className="ml-1.5 text-xs">Repeat</Badge>
}

export default function GuestsIndex({ property, profiles, meta, urls }: Props) {
  function handlePageChange(page: number) {
    router.visit(urls.index, { data: { page }, preserveScroll: true })
  }

  function handleNew() {
    router.visit(`${urls.index}/new`)
  }

  return (
    <div>
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Guests</h1>
          <p className="mt-0.5 text-sm text-muted-foreground">{property.name}</p>
        </div>
        <Button size="sm" className="gap-1.5" onClick={handleNew}>
          <UserPlus className="h-4 w-4" />
          New Guest Profile
        </Button>
      </div>

      {profiles.length === 0 ? (
        <div className="rounded-lg border border-dashed p-8 text-center">
          <p className="text-sm text-muted-foreground">No guest profiles yet.</p>
          <Button variant="outline" size="sm" className="mt-3 gap-1.5" onClick={handleNew}>
            <UserPlus className="h-4 w-4" />
            Create your first guest profile
          </Button>
        </div>
      ) : (
        <>
          <div className="rounded-lg border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Stays</TableHead>
                  <TableHead>Last Stay</TableHead>
                  <TableHead>Language</TableHead>
                  <TableHead>Email</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {profiles.map((profile) => (
                  <TableRow
                    key={profile.id}
                    className="cursor-pointer"
                    onClick={() => router.visit(profile.show_url)}
                  >
                    <TableCell className="font-medium">
                      {profile.name}
                      <RepeatBadge staysCount={profile.stays_count} />
                    </TableCell>
                    <TableCell>{profile.stays_count}</TableCell>
                    <TableCell className="text-muted-foreground">
                      {profile.last_stay_on
                        ? new Date(profile.last_stay_on).toLocaleDateString("en-GB")
                        : "—"}
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {profile.language ?? "—"}
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {profile.email ?? "—"}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          <Pagination meta={meta} onPageChange={handlePageChange} className="mt-4" />
        </>
      )}
    </div>
  )
}

GuestsIndex.layout = (page: ReactNode) => <AdminLayout>{page}</AdminLayout>
